import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import UserRolesMultiSelect from './UserRolesMultiSelect';
import {
  getAssignmentRoleName,
  getAssignmentRoleType,
  getUserActiveRoleTypes,
  isAssignmentActive,
} from '../userFilterUtils';

const UserTableRow = ({
  user,
  onEdit,
  onResetPassword,
  onToggleStatus,
  onRolesChange,
  isSelected,
  onSelect,
  onDelete,
}) => {
  const [isChangingRoles, setIsChangingRoles] = useState(false);

  const getUserActiveRoles = () => {
    if (!user?.roleAssignments?.length) {
      return [];
    }
    return user.roleAssignments
      .filter(isAssignmentActive)
      .map((assignment) => assignment?.role)
      .filter(Boolean);
  };

  const activeRoleTypes = getUserActiveRoleTypes(user);

  const handleRolesChange = async (nextRoles) => {
    if (!nextRoles?.length) return;
    setIsChangingRoles(true);
    try {
      await onRolesChange(user?.id, nextRoles);
    } catch (error) {
      console.error('Erreur lors de la mise à jour des rôles:', error);
    } finally {
      setIsChangingRoles(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      true: { bg: 'bg-success/10', text: 'text-success', label: 'Actif', icon: 'CheckCircle2' },
      false: { bg: 'bg-muted', text: 'text-muted-foreground', label: 'Inactif', icon: 'XCircle' },
      active: { bg: 'bg-success/10', text: 'text-success', label: 'Actif', icon: 'CheckCircle2' },
      inactive: { bg: 'bg-muted', text: 'text-muted-foreground', label: 'Inactif', icon: 'XCircle' },
      suspended: { bg: 'bg-warning/10', text: 'text-warning', label: 'Suspendu', icon: 'AlertCircle' },
    };

    const statusKey = typeof status === 'boolean' ? status.toString() : status;
    const config = statusConfig[statusKey] || statusConfig.active;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}
      >
        <Icon name={config?.icon} size={12} />
        {config?.label}
      </span>
    );
  };

  const getRoleBadgeConfig = (roleType) => {
    const roleConfig = {
      administrator: { bg: 'bg-primary/10', text: 'text-primary', icon: 'Shield' },
      production_supplier: { bg: 'bg-purple-500/10', text: 'text-purple-600', icon: 'Factory' },
      dentist_client: { bg: 'bg-blue-500/10', text: 'text-blue-600', icon: 'User' },
      dentist_supplier: { bg: 'bg-green-500/10', text: 'text-green-600', icon: 'Stethoscope' },
      distributor: { bg: 'bg-amber-500/10', text: 'text-amber-600', icon: 'Package' },
      staff: { bg: 'bg-accent/10', text: 'text-accent', icon: 'Users' },
    };
    return roleConfig[roleType] || roleConfig.staff;
  };

  const getRoleBadge = (roleType, roleName) => {
    const config = getRoleBadgeConfig(roleType);
    return (
      <span
        key={roleType}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${config?.bg} ${config?.text}`}
      >
        <Icon name={config?.icon} size={14} />
        {roleName || roleType}
      </span>
    );
  };

  const renderAllRoleBadges = () => {
    const activeRoles = getUserActiveRoles();

    if (activeRoles.length === 0) {
      return getRoleBadge(user?.role, null);
    }

    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        {activeRoles.map((role) =>
          getRoleBadge(getAssignmentRoleType({ role }), getAssignmentRoleName({ role }))
        )}
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date?.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Jamais connecté';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;

    return date?.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name
      ?.split(' ')
      ?.map((n) => n?.[0])
      ?.join('')
      ?.toUpperCase()
      ?.slice(0, 2);
  };

  const getRoleAssignmentsTooltip = () => {
    const activeRoles = getUserActiveRoles();
    if (activeRoles.length === 0) return 'Aucun rôle assigné';

    return activeRoles
      ?.map((role, index) => {
        const assignment = user?.roleAssignments?.find((a) => a?.role?.id === role?.id);
        const country = assignment?.country?.country_name || 'Global';
        return `${index + 1}. ${getAssignmentRoleName({ role })} (${country})`;
      })
      ?.join('\n');
  };

  return (
    <tr className="border-b border-border hover:bg-muted/50 transition-colors duration-200">
      <td className="px-4 py-3">
        <div className="flex items-center justify-center">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onSelect(user?.id, e?.target?.checked)}
            className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 cursor-pointer"
            title={`Sélectionner ${user?.name}`}
          />
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold flex-shrink-0">
            {user?.initials || getInitials(user?.name)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground truncate" title={user?.name}>
              {user?.name || 'Sans nom'}
            </p>
            <p className="text-xs text-muted-foreground truncate" title={user?.email}>
              {user?.email || 'Sans email'}
            </p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-2 min-w-[220px]">
          <UserRolesMultiSelect
            value={activeRoleTypes}
            onChange={handleRolesChange}
            disabled={isChangingRoles}
            label=""
            description=""
            className="text-xs"
            placeholder="Rôles…"
          />
          <div title={getRoleAssignmentsTooltip()}>{renderAllRoleBadges()}</div>
        </div>
      </td>
      <td className="px-4 py-3">{getStatusBadge(user?.status)}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon name="Clock" size={14} className="text-muted-foreground flex-shrink-0" />
          <p className="text-sm text-foreground" title={formatDate(user?.lastLogin)}>
            {formatDateTime(user?.lastLogin)}
          </p>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon name="Calendar" size={14} className="text-muted-foreground flex-shrink-0" />
          <p className="text-sm text-muted-foreground">{formatDate(user?.createdAt)}</p>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(user)}
            iconName="Edit"
            iconSize={16}
            className="hover:bg-primary/10 hover:text-primary"
            title="Modifier l'utilisateur"
          >
            <span className="sr-only">Modifier l'utilisateur</span>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onResetPassword(user)}
            iconName="KeyRound"
            iconSize={16}
            className="hover:bg-blue-500/10 hover:text-blue-600"
            title="Réinitialiser le mot de passe"
          >
            <span className="sr-only">Réinitialiser le mot de passe</span>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onToggleStatus(user)}
            iconName={user?.status === 'active' || user?.status === true ? 'UserX' : 'UserCheck'}
            iconSize={16}
            className="hover:bg-orange-500/10 hover:text-orange-600"
            title={user?.status === 'active' || user?.status === true ? 'Désactiver' : 'Activer'}
          >
            <span className="sr-only">Basculer le statut</span>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete?.(user)}
            iconName="Trash2"
            iconSize={16}
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            title="Supprimer l'utilisateur"
          >
            <span className="sr-only">Supprimer l'utilisateur</span>
          </Button>
        </div>
      </td>
    </tr>
  );
};

export default UserTableRow;
